import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component,
  ComponentRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  MatSelectionList,
  MatListOption,
  MatListItemIcon,
  MatListItemTitle,
} from '@angular/material/list';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SfoUiJSONSchema7 } from '../../metadata.model';
import { FormService } from '../service/form.service';
import { DrawerViewContentComponent } from './drawer-view-content/drawer-view-content.component';
import { MatDrawerContainer, MatDrawer, MatDrawerContent } from '@angular/material/sidenav';
import { AsyncPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

interface Tab {
  id: string;
  label: string;
  description?: string;
  icon?: string;
  tag?: string;
}

@Component({
  selector: 'sfo-drawer-view',
  templateUrl: './drawer-view.component.html',
  styleUrls: ['./drawer-view.component.scss'],
  imports: [
    MatDrawerContainer,
    MatDrawer,
    MatSelectionList,
    MatListOption,
    MatTooltip,
    MatIcon,
    MatListItemIcon,
    MatListItemTitle,
    MatDrawerContent,
    AsyncPipe,
  ],
})
export class DrawerViewComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private currentComponent?: ComponentRef<DrawerViewContentComponent>;
  private lastRenderedControl: string | null = null;

  schema$ = new BehaviorSubject<SfoUiJSONSchema7 | null>(null);
  isSmallScreen$ = this.breakpointObserver
    .observe(['(max-width: 52em)'])
    .pipe(map((result) => result.matches));

  @ViewChild('vcr', { static: true, read: ViewContainerRef }) vcr!: ViewContainerRef;
  @ViewChild('matSelectionList') matSelectionList: MatSelectionList;

  @Input() aFormControl: FormGroup;
  @Input() set schema(data: SfoUiJSONSchema7 | null) {
    this.schema$.next(data);
    // Update tabs synchronously so first CD sees stable bindings (avoids NG0100)
    this.initializeTabs();
  }

  activeTab: string = '';
  tabs: Tab[] = [];
  advancedMode$ = this.formService.advancedMode$;

  constructor(
    private formService: FormService,
    private breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.schema$.subscribe(() => {
        this.initializeTabs();
        this.renderDynamicComponent('');
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.reset();
    this.schema$.complete();
  }

  private initializeTabs(): void {
    this.reset();
    this.tabs = [];
    this.activeTab = '';
    this.lastRenderedControl = null;

    if (this.matSelectionList) {
      this.matSelectionList.deselectAll();
    }

    const schema = this.schema$.getValue();
    if (!schema || !schema.properties) {
      return;
    }

    this.tabs = Object.entries(schema.properties)
      .filter(([key, value]) => !value?.['_ui_hide'])
      .map(([key, value]) => ({
        label: value?.title ?? key,
        icon: value['_ui_icon'],
        id: key,
        tag: value['_ui_tag'],
        description: value?.description,
      }));
  }

  selectConfiguration(control: string): void {
    if (this.activeTab === control) {
      return;
    }

    this.activeTab = control;
    this.renderDynamicComponent(control);
  }

  private renderDynamicComponent(control: string): void {
    const schema = this.schema$.getValue();

    if (!schema || this.lastRenderedControl === control) {
      return;
    }

    this.destroyCurrentComponent();

    this.currentComponent = this.vcr.createComponent(DrawerViewContentComponent, {
      projectableNodes: [[document.createTextNode('')]],
    });
    const instance = this.currentComponent.instance;
    instance.schema = schema;
    instance.aFormControl = this.aFormControl;
    instance.control = control;

    this.lastRenderedControl = control;
    this.currentComponent.changeDetectorRef.detectChanges();
  }

  private destroyCurrentComponent(): void {
    if (this.currentComponent) {
      this.currentComponent.destroy();
      this.currentComponent = undefined;
      this.lastRenderedControl = null;
    }
  }

  private reset(): void {
    this.destroyCurrentComponent();
    if (this.vcr) {
      this.vcr.clear();
    }
  }

  trackByLabel(_: number, tab: Tab): string {
    return tab.id;
  }

  toggleAdvanceMode(): void {
    this.formService.toggleAdvancedMode();
  }
}
